import {
  type AnnouncementPlacementCapability,
  type AnnouncementRenderedPlacement,
  DEFAULT_MODAL_BACKGROUND,
  PLACEMENT_CAPABILITIES,
} from '@gto/announcement-ui'
import type {
  AnnouncementAdminSchema,
  AnnouncementAudienceChoice,
  AnnouncementBackgroundMobileModeChoice,
  AnnouncementBackgroundRepeatChoice,
  AnnouncementBackgroundSizeChoice,
  AnnouncementBodyStyleChoice,
  AnnouncementCountdownExpiryChoice,
  AnnouncementCtaTargetChoice,
  AnnouncementInsiderGateChoice,
  AnnouncementModalWidthChoice,
  AnnouncementToasterMediaLayoutChoice,
  AnnouncementWritablePlacementChoice,
} from '@gto/api-client/admin/react-query'
import { paperColors } from '@gto/ui/theme/design-tokens'

import { validateCtaLinkShape } from './announcement-cta-link'
// Re-exported so every existing caller still reaches the caps through the form's own
// module; they moved out so the block validator can share them without an import cycle.
export {
  formatBytesLimit,
  IMAGE_ACCEPT,
  IMAGE_MAX_BYTES,
  IMAGE_MAX_LONGEST_EDGE_PX,
  validateMediaSize,
  VIDEO_ACCEPT,
  VIDEO_MAX_BYTES,
} from './announcement-media-limits'
import { modalBackgroundFormErrors } from './announcement-modal-background'
import {
  DEFAULT_PACING_GAP_UNIT,
  PACING_GAP_UNIT_LABEL,
  pacingGapFromHours,
  pacingGapMaxValue,
  pacingGapToHours,
  type PacingGapUnit,
} from './announcement-pacing-gap'
import {
  campaignDayMidnightIfUniform,
  campaignWallClockToInstant,
  campaignZoneAbbreviationAt,
  formatInReaderZone,
  instantToCampaignWallClock,
  type WallClock,
} from './announcement-time-zone'
import { blockDraftsFrom } from './block-editor/block-drafts'
import type { BlockDraft } from './block-editor/block-types'
import { blocksSummaryError } from './block-editor/block-validation'
import { storedPlans } from './plan-matrix/plan-cells'

/**
 * The first authored gradient is a scrim rather than the renderer's opaque
 * fallback. Both use the same paper tokens, with 40%/80% alpha appended, so a
 * newly uploaded artwork shows immediately while copy remains readable above it.
 * An unauthored modal still uses `DEFAULT_MODAL_BACKGROUND` unchanged.
 */
export const DEFAULT_MODAL_ARTWORK_SCRIM = {
  start: `${paperColors.elevation3}66`,
  end: `${paperColors.elevation0}CC`,
} as const

/**
 * `name` doubles as the per-user dismissal key, so it has to be a stable slug —
 * mirror the shape the BE expects and reject typos before submit. The BE still
 * owns uniqueness and the post-publish rename lock.
 */
export const NAME_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/

/**
 * `name` is also the `announcement_name` GA4 parameter, whose own ceiling is 100
 * characters — generous is fine, unbounded is not, because a longer key would be
 * truncated in analytics while staying whole as the dismissal key, and the two
 * would stop lining up. Same number the BE column carries (`NAME_MAX_LENGTH`).
 */
export const NAME_MAX_LENGTH = 100

/**
 * `tag` groups several announcements into one campaign so the admin list can be
 * filtered down to it. Admin-only — it is absent from the customer payload — and
 * matched EXACTLY by the list endpoint, which is why the form trims it: a
 * trailing space would produce a tag no filter selection can ever reach.
 */
export const TAG_MAX_LENGTH = 50

/** Mirrors of the BE column widths, so an over-long value fails here first. */
export const TITLE_MAX_LENGTH = 200
export const TITLE_HIGHLIGHT_MAX_LENGTH = 200
export const CTA_TEXT_MAX_LENGTH = 100
export const CTA_LINK_MAX_LENGTH = 500
export const REQUIRED_FLAG_MAX_LENGTH = 64

/**
 * NOT a column mirror — the contract declares no `maxLength` on
 * `route_prefixes.items`. A client-side sanity cap: a "prefix" this long is a
 * pasted full URL or a mangled paste, not a route, and it matches nothing.
 */
export const ROUTE_PREFIX_MAX_LENGTH = 200

/** `priority` is a Postgres smallint on the BE. */
export const PRIORITY_MIN = -32768
export const PRIORITY_MAX = 32767

/**
 * The contract's own ceiling on `auto_close_seconds` (`0..32767`). Enforced here
 * so an over-long timer is a message on the field rather than a raw 422; the
 * floor is stricter in the UI, where 0 means "no auto-close" and is expressed by
 * leaving the field blank.
 */
export const AUTO_CLOSE_MAX_SECONDS = 32767

/**
 * The same smallint ceiling the contract puts on both account-age bounds
 * (`0..32767`, ~90 years). Enforced here for the reason `contractAutoClose`
 * spells out: sent unchecked it is a raw 422 whose toast names no field, on a
 * form with two dozen of them.
 */
export const ACCOUNT_AGE_MAX_DAYS = 32767

/**
 * The contract's `title_size_px` bounds, so out of range is a field message here
 * rather than a 422.
 *
 * Mirrored rather than imported from the generated zod endpoints: those constants
 * are named by orval's dedup counter (`…MinOne`), and the message below reads as
 * prose either way. The mirror is not left to trust — a test asserts it equals
 * every generated pair, so a backend that widens the column turns this into a red
 * test at regen time rather than a form that rejects what the API now accepts.
 *
 * `DashboardSlideTitle` keeps its own private clamp on the same numbers. That one
 * defends the renderer against unvalidated data; this one decides what may be
 * saved. Same values today, different authorities — do not collapse them.
 */
export const TITLE_SIZE_MIN_PX = 16
export const TITLE_SIZE_MAX_PX = 48

/**
 * Mirror of `COUNTDOWN_LABEL_MAX_LENGTH` on the BE column. Same standing as the
 * title-size bounds above: the API rejects independently, and this only exists so
 * the operator learns before submitting.
 */
export const COUNTDOWN_LABEL_MAX_LENGTH = 60

/**
 * The authored title size the contract accepts, or null for "let the renderer
 * decide" — blank, mid-edit, or out of range.
 *
 * Validation, both payload builders and the preview read this one predicate. Drift
 * between them shows up as a preview that renders a size the save rejects, or a
 * payload that ships one the operator was never told was invalid.
 */
export const parseTitleSize = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (trimmed === '') {
    return null
  }

  const parsed = Number(trimmed)

  return Number.isInteger(parsed) && parsed >= TITLE_SIZE_MIN_PX && parsed <= TITLE_SIZE_MAX_PX
    ? parsed
    : null
}

/**
 * The form's own value shape: every field a string or boolean, because that is
 * what the controls hand back. Dates are split into a wall-clock day and a
 * `HH:mm` time so the day can drive the date-range picker while the time drives
 * its own field, and neither has to round-trip through a `Date` to stay editable.
 */
export type AnnouncementFormValues = {
  name: string
  /** Free-text campaign grouping; empty = untagged. */
  tag: string
  placement: AnnouncementWritablePlacementChoice
  title: string
  titleHighlight: string
  /** Blank = leave it to the renderer's own default. */
  titleSizePx: string
  /** Modal only; how wide the dialog renders. */
  modalWidth: AnnouncementModalWidthChoice
  /** Toaster only; whether the media keeps the card's padding or runs to its edges. */
  toasterMediaLayout: AnnouncementToasterMediaLayoutChoice
  /** Toaster only; whether the card draws the "New" chip. */
  toasterShowNewLabel: boolean
  modalBackgroundEnabled: boolean
  modalBackgroundStart: string
  modalBackgroundEnd: string
  modalBackgroundAngle: string
  modalBackgroundSize: AnnouncementBackgroundSizeChoice
  modalBackgroundPosition: string
  modalBackgroundRepeat: AnnouncementBackgroundRepeatChoice
  modalBackgroundMobileMode: AnnouncementBackgroundMobileModeChoice
  body: string
  bodyStyle: AnnouncementBodyStyleChoice
  /**
   * The countdown deadline, split into a wall-clock day and a `HH:mm` time the
   * same way the schedule is. Blank day = no countdown, which is what makes
   * clearing one a single edit.
   */
  countdownDate: string
  countdownTime: string
  /** Caption above the tiles; blank renders none. */
  countdownLabel: string
  countdownExpiry: AnnouncementCountdownExpiryChoice
  ctaText: string
  ctaLink: string
  ctaTarget: AnnouncementCtaTargetChoice
  secondaryCtaText: string
  secondaryCtaLink: string
  routePrefixes: string
  routeExcludePrefixes: string
  audience: AnnouncementAudienceChoice
  audienceInverted: boolean
  /**
   * `SubscriptionPlan` ids within the Paid audience, ascending. Blank = every
   * paid plan; inversion negates the match.
   */
  audiencePlans: number[]
  insiderGate: AnnouncementInsiderGateChoice
  /** Whole days since signup; blank = no floor. Held as a string like `priority`. */
  minAccountAgeDays: string
  /** Whole days since signup; blank = no ceiling. */
  maxAccountAgeDays: string
  requiredFlag: string
  requiredFlagInverted: boolean
  locales: string
  localesInverted: boolean
  dismissible: boolean
  /** `YYYY-MM-DD` in the campaign zone (Europe/Berlin), never the browser's. */
  startDate: string
  /** `HH:mm` in the campaign zone (Europe/Berlin), never the browser's. */
  startTime: string
  /** Empty = runs indefinitely. */
  endDate: string
  endTime: string
  autoCloseSeconds: string
  /**
   * How long after the viewer closes the previous announcement of this placement
   * this one may appear. Blank = show as soon as it is otherwise eligible.
   *
   * Authored as a value plus a unit and stored as hours, because a month and a
   * year are not fixed spans — `announcement-pacing-gap.ts` owns the conversion.
   */
  pacingGapValue: string
  pacingGapUnit: PacingGapUnit
  priority: string
  isPreview: boolean
  isPaused: boolean
  /**
   * The modal's content, and the ONE field no other placement reads — every
   * other surface renders the flat title/body above instead.
   *
   * Held as editor drafts rather than as the contract's blocks because a draft
   * carries the identity a reorder needs; `toBlocksPayload` strips that on the
   * way out. Kept across a placement switch like every other value, so trying
   * surfaces out costs nothing — what stops a stranded list reaching a surface
   * that cannot render it is `buildUpdatePatch`, which clears `blocks` on the
   * way to a non-modal placement.
   */
  blocks: BlockDraft[]
}

export type FormFieldError =
  | 'audience'
  | 'audiencePlans'
  | 'autoCloseSeconds'
  | 'blocks'
  | 'countdownDate'
  | 'countdownLabel'
  | 'ctaLink'
  | 'ctaText'
  | 'endDate'
  | 'image'
  | 'locales'
  | 'maxAccountAgeDays'
  | 'minAccountAgeDays'
  | 'modalBackgroundAngle'
  | 'modalBackgroundEnd'
  | 'modalBackgroundImage'
  | 'modalBackgroundImageMobile'
  | 'modalBackgroundPosition'
  | 'modalBackgroundStart'
  | 'name'
  | 'pacingGap'
  | 'priority'
  | 'requiredFlag'
  | 'routeExcludePrefixes'
  | 'routePrefixes'
  | 'secondaryCtaLink'
  | 'secondaryCtaText'
  | 'startDate'
  | 'tag'
  | 'title'
  | 'titleHighlight'
  | 'titleSizePx'
  | 'video'

export type FormErrors = Partial<Record<FormFieldError, string>>

export type ValidationResult = { ok: true } | { ok: false; errors: FormErrors }

/**
 * A route prefix / locale list typed as free text: one entry per line, or comma
 * separated, whichever the operator reaches for. Blank entries are dropped so a
 * trailing newline never becomes an empty prefix — which on the BE would match
 * every route and silently widen the campaign.
 */
export const parseList = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')

/** Inverse of `parseList` for prefilling the textarea from a stored list. */
export const formatList = (values: string[] | undefined): string => (values ?? []).join('\n')

const pad2 = (value: number): string => String(value).padStart(2, '0')

/**
 * `YYYY-MM-DD` off a `Date`'s browser-local parts. Paired only with the DS pickers,
 * which hand back a `Date` carrying the calendar fields the operator clicked and
 * nothing else — never with a stored instant, whose day belongs to the campaign
 * zone and is read by `fromInstant`.
 */
const toLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

/** `HH:mm` off a `Date`'s browser-local parts, paired with the DS time field. */
const toLocalTime = (date: Date): string => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const TIME_PATTERN = /^(\d{2}):(\d{2})$/

/** Reads a `YYYY-MM-DD` string into its three numbers, or null when it is not one. */
const readLocalDate = (value: string): { year: number; month: number; day: number } | null => {
  const match = DATE_PATTERN.exec(value.trim())
  if (match === null) {
    return null
  }

  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

/**
 * A wall-clock day plus a time-of-day, both in the campaign zone, as the UTC
 * instant the BE stores. Returns null when either half is missing or unreadable so
 * the caller can say so rather than send a silently-wrong window.
 *
 * Converted through `campaignWallClockToInstant` rather than the `Date`
 * constructor's local-parts overload, so what an operator types means the same
 * instant whichever zone their laptop is in — see `announcement-time-zone.ts`.
 */
export const toInstant = (localDate: string, localTime: string): string | null => {
  const day = readLocalDate(localDate)
  if (day === null) {
    return null
  }

  const timeMatch = TIME_PATTERN.exec(localTime.trim())
  if (timeMatch === null) {
    return null
  }

  const hours = Number(timeMatch[1])
  const minutes = Number(timeMatch[2])
  if (hours > 23 || minutes > 59) {
    return null
  }

  const instantMs = campaignWallClockToInstant({ ...day, hours, minutes })
  if (Number.isNaN(instantMs)) {
    return null
  }

  return new Date(instantMs).toISOString()
}

/** Splits a stored instant back into the two campaign-zone fields the form edits. */
export const fromInstant = (iso: string | null | undefined): { date: string; time: string } => {
  if (iso === null || iso === undefined || iso.trim() === '') {
    return { date: '', time: '' }
  }

  const parsed = Date.parse(iso)
  if (Number.isNaN(parsed)) {
    return { date: '', time: '' }
  }

  return formatWallClock(instantToCampaignWallClock(parsed))
}

/** The two strings the form's date and time fields hold, from a campaign wall clock. */
const formatWallClock = (wall: WallClock): { date: string; time: string } => ({
  date: `${wall.year}-${pad2(wall.month)}-${pad2(wall.day)}`,
  time: `${pad2(wall.hours)}:${pad2(wall.minutes)}`,
})

/** `YYYY-MM-DD` → the `Date` the DS date pickers take, at local midnight. */
export const toPickerDate = (localDate: string): Date | null => {
  const parts = readLocalDate(localDate)
  if (parts === null) {
    return null
  }

  const date = new Date(parts.year, parts.month - 1, parts.day)

  return Number.isNaN(date.getTime()) ? null : date
}

/** Inverse of `toPickerDate`; an unset or unreadable picker value clears the day. */
export const fromPickerDate = (date: Date | null): string => {
  if (date === null || Number.isNaN(date.getTime())) {
    return ''
  }

  return toLocalDate(date)
}

/**
 * `HH:mm` → the `Date` the DS time field takes. The day is a fixed placeholder:
 * the field only ever reads and writes the time-of-day, and pinning the date
 * keeps the control from drifting a day when the clock crosses midnight mid-edit.
 */
export const toPickerTime = (localTime: string): Date | null => {
  const match = TIME_PATTERN.exec(localTime.trim())
  if (match === null) {
    return null
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) {
    return null
  }

  return new Date(2000, 0, 1, hours, minutes, 0, 0)
}

/** Inverse of `toPickerTime`; a half-typed or cleared field reads as empty. */
export const fromPickerTime = (date: Date | null): string => {
  if (date === null || Number.isNaN(date.getTime())) {
    return ''
  }

  return toLocalTime(date)
}

/**
 * The instant the announcement starts, or null while either half is unreadable.
 *
 * A blank time has no instant and falls back to the start day, a coarser answer
 * being better than none mid-edit — but only where the day HAS one answer. On a
 * daylight-saving changeover day it does not, and `campaignDayMidnightIfUniform`
 * returns null rather than pick the half that happens to contain midnight; the
 * caption then reads its zone-less wording until a time is typed.
 *
 * That coarseness suits a NAME and not a timestamp, which is why only the label
 * takes this path — see `readerZoneHintFor`.
 */
const startInstantMs = (localDate: string, localTime: string): number | null => {
  const day = readLocalDate(localDate)
  if (day === null) {
    return null
  }

  const instant = toInstant(localDate, localTime)
  if (instant === null) {
    return campaignDayMidnightIfUniform(day)
  }

  const instantMs = Date.parse(instant)

  return Number.isNaN(instantMs) ? null : instantMs
}

/**
 * `CET` or `CEST` for the day this campaign STARTS — not for today. On opposite
 * sides of a daylight-saving boundary those are different names for different
 * offsets, so labelling a January campaign with today's summer abbreviation would
 * put an hour of drift in the very copy meant to prevent it.
 *
 * From the form's own fields rather than the clock, which also keeps it out of the
 * React Compiler's way: these are state, so the label re-derives when the schedule
 * moves (a `Date.now()` read during render is what the compiler freezes — see
 * C-1852's `useTickingNow` extraction).
 */
export const campaignZoneLabelFor = (
  localDate: string,
  localTime: string,
): 'CET' | 'CEST' | null => {
  const instantMs = startInstantMs(localDate, localTime)

  return instantMs === null ? null : campaignZoneAbbreviationAt(instantMs)
}

/**
 * The same start moment on the reader's own clock — `"2 Jul 2026, 08:30 GMT-4"` — so
 * an operator outside Central Europe can check a CET schedule against the day they
 * are actually living. Null when their zone keeps the same clock as the campaign
 * zone, which is most of this team.
 *
 * Silent until BOTH halves are typed, unlike the CET/CEST label beside it: the
 * label is a name a whole ordinary day agrees on, but this renders an hour and a
 * minute. Falling back to midnight here would tell an operator who cleared the
 * time that their campaign starts at 18:00 the previous evening — a specific
 * moment they never entered, and one the label's coarseness does not excuse.
 *
 * `timeZone` defaults to the runtime's own; only tests pass it, because the suite
 * pins TZ=UTC and a hint worth asserting needs a real offset.
 */
export const readerZoneHintFor = (
  localDate: string,
  localTime: string,
  timeZone?: string,
): string | null => {
  const instant = toInstant(localDate, localTime)
  if (instant === null) {
    return null
  }

  const instantMs = Date.parse(instant)

  return Number.isNaN(instantMs) ? null : formatInReaderZone(instantMs, timeZone)
}

const isBlank = (value: string): boolean => value.trim() === ''

const validateOptionalLength = (
  errors: FormErrors,
  field: FormFieldError,
  value: string,
  max: number,
  label: string,
): void => {
  if (value.length > max) {
    errors[field] = `${label} must be at most ${max} characters.`
  }
}

/**
 * One account-age bound, recording its own error and reporting the number back
 * for the min-vs-max comparison.
 *
 * `null` covers both "blank" and "already rejected" on purpose: the cross-field
 * check needs two good numbers, and a bound that is not a whole day should be
 * reported as malformed once rather than also as being on the wrong side of the
 * other bound.
 */
const parseAgeBound = (
  errors: FormErrors,
  field: 'maxAccountAgeDays' | 'minAccountAgeDays',
  value: string,
): number | null => {
  const raw = value.trim()
  if (isBlank(raw)) {
    return null
  }

  const parsed = Number(raw)
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > ACCOUNT_AGE_MAX_DAYS) {
    errors[field] =
      `Account age must be a whole number of days between 0 and ${ACCOUNT_AGE_MAX_DAYS}.`

    return null
  }

  return parsed
}

/**
 * Re-exported so the form's call sites read their placement rules from the same
 * module as `placementSupports`, `CAPABILITY_BY_FIELD` and `visibleErrors`. Why the
 * matrix lives in the library is documented at its definition.
 */
export { type AnnouncementPlacementCapability, PLACEMENT_CAPABILITIES }

/**
 * A placement the form can be asked to author, that the library has no renderer
 * for, must not compile.
 *
 * Several `Record<AnnouncementWritablePlacementChoice, ...>` maps in this feature
 * already fail to compile when the generated enum grows, so the ADDITION is caught
 * without this. What none of them check is the other side: each is satisfied by
 * adding an entry, and stays satisfied when the new surface has no renderer in the
 * library and no row in the matrix — at which point the form offers a placement
 * whose every gated control is hidden and every gated rule skipped. This is the
 * only thing tying what the form can author to what something can actually draw.
 *
 * Tuple-wrapped because `never` is assignable to every member, so the shorter
 * unwrapped `Exclude<...>` annotation passes exactly when the two have diverged.
 */
type EveryWritablePlacementRenders = [
  Exclude<AnnouncementWritablePlacementChoice, AnnouncementRenderedPlacement>,
] extends [never]
  ? true
  : false
const _everyWritablePlacementRenders: EveryWritablePlacementRenders = true

/**
 * The capability each error's field belongs to, or null where every surface
 * renders it.
 *
 * A total `Record` for the same reason `ANCHOR_ID_BY_ERROR` is one: adding a
 * `FormFieldError` without deciding whether a placement can hide it does not
 * compile. The alternative is an error nobody can clear, on a control that is
 * not on the page.
 */
const CAPABILITY_BY_FIELD: Record<FormFieldError, AnnouncementPlacementCapability | null> = {
  audience: null,
  audiencePlans: null,
  autoCloseSeconds: 'autoClose',
  blocks: 'blocks',
  countdownDate: 'countdown',
  countdownLabel: 'countdown',
  ctaLink: 'primaryCta',
  ctaText: 'primaryCta',
  endDate: null,
  image: 'image',
  locales: null,
  maxAccountAgeDays: null,
  minAccountAgeDays: null,
  modalBackgroundAngle: 'blocks',
  modalBackgroundEnd: 'blocks',
  modalBackgroundImage: 'blocks',
  modalBackgroundImageMobile: 'blocks',
  modalBackgroundPosition: 'blocks',
  modalBackgroundStart: 'blocks',
  name: null,
  pacingGap: 'pacingGap',
  priority: null,
  requiredFlag: null,
  routeExcludePrefixes: null,
  routePrefixes: null,
  secondaryCtaLink: 'secondaryCta',
  secondaryCtaText: 'secondaryCta',
  startDate: null,
  tag: null,
  title: 'title',
  titleHighlight: 'titleHighlight',
  titleSizePx: 'titleSize',
  video: 'video',
}

/**
 * The error keys in the order their controls appear on the page — which is the
 * order the operator reads, and is unrelated to the insertion order
 * `Object.keys(errors)` would hand back.
 *
 * Lives here rather than beside the scroll logic that first needed it because it
 * is now also the typed key list `visibleErrors` iterates. Kept in step with
 * `ANCHOR_ID_BY_ERROR` by its own test rather than by the compiler: TypeScript
 * can enforce that a tuple has every key, but not readably.
 */
export const ERROR_ORDER: readonly FormFieldError[] = [
  'name',
  'tag',
  'priority',
  'title',
  'titleHighlight',
  'titleSizePx',
  'countdownDate',
  'countdownLabel',
  'blocks',
  'modalBackgroundStart',
  'modalBackgroundEnd',
  'modalBackgroundAngle',
  'modalBackgroundPosition',
  'modalBackgroundImage',
  'modalBackgroundImageMobile',
  'ctaText',
  'ctaLink',
  'secondaryCtaText',
  'secondaryCtaLink',
  'image',
  'video',
  'audience',
  'requiredFlag',
  'audiencePlans',
  'minAccountAgeDays',
  'maxAccountAgeDays',
  'locales',
  'routePrefixes',
  'routeExcludePrefixes',
  'startDate',
  'endDate',
  'autoCloseSeconds',
  'pacingGap',
]

/**
 * The errors the operator can actually see and act on, given the placement.
 *
 * ONE projection, read by every consumer — the invalid-field counter, each
 * field's `error` prop, and the submit path. They used to decide separately, and
 * a per-field suppression that the counter did not know about produced a red "1
 * field needs fixing" above a form with no visible error and nothing wrong with
 * it. Anything reading raw `errors` for display can drift the same way again.
 *
 * An error on a gated field is dropped rather than hidden: the control is not
 * rendered, so the operator cannot reach it to clear it, and
 * `validateAnnouncementForm` skips that field's rules anyway — keeping the
 * error would block a save the form considers valid. This is what makes hiding
 * the control safe rather than a trap. Dropped keys are ABSENT, not
 * present-and-undefined, because the submit path decides on
 * `Object.keys(...).length`.
 */
export const visibleErrors = (
  errors: FormErrors,
  placement: AnnouncementWritablePlacementChoice,
): FormErrors => {
  const visible: FormErrors = {}

  // Walks the KEY LIST rather than `Object.entries(errors)`, whose keys type as
  // plain `string`. The difference is not cosmetic: a foreign key would look up
  // `undefined` in `CAPABILITY_BY_FIELD`, pass the `!== null` test, and throw
  // inside `placementSupports`.
  for (const field of ERROR_ORDER) {
    const message = errors[field]
    const capability = CAPABILITY_BY_FIELD[field]
    const isGated = capability !== null && !placementSupports(placement, capability)

    if (message !== undefined && !isGated) {
      visible[field] = message
    }
  }

  return visible
}

/**
 * Whether the placement's renderer reads this field at all.
 *
 * `some` rather than `includes` because the matrix rows are literal tuples: a row
 * typed `readonly ['modal']` rejects a wider placement as an `includes` argument.
 *
 * The swap is not free. `includes` required its argument to be ASSIGNABLE to the
 * element type, while `===` needs only that the two overlap, so nothing in this
 * body rejects a placement the library cannot render. The parameter's own type and
 * `EveryWritablePlacementRenders` above are what carry that now.
 */
export const placementSupports = (
  placement: AnnouncementWritablePlacementChoice,
  capability: AnnouncementPlacementCapability,
): boolean =>
  PLACEMENT_CAPABILITIES[capability].placements.some((supported) => supported === placement)

/**
 * Why the field is inert for this placement, or `undefined` where it applies.
 *
 * The form does not show this next to the control — it renders no inert controls
 * at all. It exists for the one case where something inert still has to be
 * explained: a media file already staged when the operator switches to a surface
 * that will not upload it.
 */
export const capabilityInertReason = (
  placement: AnnouncementWritablePlacementChoice,
  capability: AnnouncementPlacementCapability,
): string | undefined =>
  placementSupports(placement, capability)
    ? undefined
    : PLACEMENT_CAPABILITIES[capability].inertReason

/**
 * The gap the form would SAVE from these values, in stored hours.
 *
 * The BE REJECTS a gap on the dashboard banner, because a slide has no close
 * control to measure one from. A gap typed under `toaster` and left behind by a
 * switch to `dashboard_banner` would otherwise fail the whole save, pointing at a
 * control the form no longer renders — so the gate drops it instead.
 *
 * Null rather than 0 for a cleared field: the BE accepts 0 and treats it as no
 * gap, but null is what an unset nullable column means.
 *
 * Anything that reads the PENDING gap has to agree with what the save sends, or
 * the order panel shows a wait the save is about to drop. One function, so the
 * payload and the panel cannot drift apart on it.
 */
export const pacingGapHoursFromValues = (values: AnnouncementFormValues): number | null =>
  placementSupports(values.placement, 'pacingGap')
    ? pacingGapToHours({ value: values.pacingGapValue, unit: values.pacingGapUnit })
    : null

/**
 * Every rule that can be decided from the scalar fields alone. Media and the
 * missing-dashboard-image warning are deliberately NOT here: one needs the picked
 * `File`, and the other is a warning the item still saves through.
 *
 * Rules for a field the placement cannot render are SKIPPED. The form hides those
 * controls without clearing them, so a value left behind by an earlier placement
 * — a highlight that no longer matches the title, a secondary CTA with no link —
 * would otherwise block every save with an error pointing at a control that is
 * not even on the page.
 *
 * What keeps that safe is NOT the backend. Three of the skipped rules are ours
 * alone: the highlight-must-be-a-substring check, text-requires-a-link, and the
 * CTA link shape. The contract enforces the length caps and the auto-close bounds
 * and nothing else, so a skipped rule is genuinely unenforced while it is
 * skipped. Two things make that acceptable:
 *
 *   - The gate re-arms on the LIVE placement. Every skip below reads
 *     `values.placement`, so the moment an operator switches back to a surface
 *     that renders the field, the rule fires again and blocks the save. A value
 *     can never reach a renderer that reads it without passing its own rule.
 *   - The customer surfaces defend themselves. `isSafeCtaLink` drops an unsafe
 *     link and renders no button rather than an unvalidated `href`, and a
 *     highlight that is not in the title simply paints no accent. These rules
 *     exist to catch a mistake early, not to be the only thing standing between
 *     a typo and production.
 */
export const validateAnnouncementForm = (
  values: AnnouncementFormValues,
  { requireName }: { requireName: boolean },
): ValidationResult => {
  const errors: FormErrors = {}

  if (requireName) {
    const name = values.name.trim()
    if (isBlank(name)) {
      errors.name = 'Name is required.'
    } else if (name.length > NAME_MAX_LENGTH) {
      errors.name = `Name must be at most ${NAME_MAX_LENGTH} characters.`
    } else if (!NAME_PATTERN.test(name)) {
      errors.name = 'Use lowercase letters, digits, hyphens or underscores (e.g. plo-launch-2026).'
    }
  }

  // Checked on the TRIMMED value, because that is what `buildCreateBody` sends —
  // validating the raw string would reject a tag the BE would have accepted.
  validateOptionalLength(errors, 'tag', values.tag.trim(), TAG_MAX_LENGTH, 'Tag')

  if (placementSupports(values.placement, 'title')) {
    if (isBlank(values.title)) {
      errors.title = 'Title is required.'
    } else {
      validateOptionalLength(errors, 'title', values.title, TITLE_MAX_LENGTH, 'Title')
    }
  }

  const highlight = values.titleHighlight.trim()
  if (!isBlank(highlight) && placementSupports(values.placement, 'titleHighlight')) {
    validateOptionalLength(
      errors,
      'titleHighlight',
      values.titleHighlight,
      TITLE_HIGHLIGHT_MAX_LENGTH,
      'Highlight',
    )
    // The customer renderer accents the highlight by finding it INSIDE the title.
    // A highlight that is not a substring renders no accent at all, which reads
    // in production as "the accent feature is broken" rather than as a typo.
    if (errors.titleHighlight === undefined && !values.title.includes(highlight)) {
      errors.titleHighlight = 'Highlight must be an exact piece of the title.'
    }
  }

  // Skipped off a modal for the same reason every other gated rule is: the
  // editor is not rendered there, so an error it raised would name blocks the
  // operator cannot see, let alone fix. What stops those drafts reaching a
  // surface that cannot render them is `buildUpdatePatch`, which clears `blocks`
  // on the way out of the modal placement.
  if (placementSupports(values.placement, 'blocks')) {
    const blocksError = blocksSummaryError(values.blocks)
    // Assigned only when there IS one. This function reports validity as
    // `Object.keys(errors).length === 0`, so a present-but-undefined key is
    // indistinguishable from a real error to it — and a clean modal would fail
    // to save with nothing on screen saying why.
    if (blocksError !== undefined) {
      errors.blocks = blocksError
    }

    if (values.modalBackgroundEnabled) {
      Object.assign(errors, modalBackgroundFormErrors(values))
    }
  }

  const validatesPrimaryCta = placementSupports(values.placement, 'primaryCta')
  if (validatesPrimaryCta) {
    validateOptionalLength(errors, 'ctaText', values.ctaText, CTA_TEXT_MAX_LENGTH, 'CTA text')
    validateOptionalLength(errors, 'ctaLink', values.ctaLink, CTA_LINK_MAX_LENGTH, 'CTA link')
  }
  const validatesSecondaryCta = placementSupports(values.placement, 'secondaryCta')
  if (validatesSecondaryCta) {
    validateOptionalLength(
      errors,
      'secondaryCtaText',
      values.secondaryCtaText,
      CTA_TEXT_MAX_LENGTH,
      'Secondary CTA text',
    )
    validateOptionalLength(
      errors,
      'secondaryCtaLink',
      values.secondaryCtaLink,
      CTA_LINK_MAX_LENGTH,
      'Secondary CTA link',
    )
  }
  validateOptionalLength(
    errors,
    'requiredFlag',
    values.requiredFlag,
    REQUIRED_FLAG_MAX_LENGTH,
    'Required flag',
  )

  // A CTA label with no target is a dead button in the customer app: it renders,
  // it is clickable, and it goes nowhere.
  if (validatesPrimaryCta && !isBlank(values.ctaText) && isBlank(values.ctaLink)) {
    errors.ctaLink = 'A CTA with a label needs a link.'
  }
  if (
    validatesSecondaryCta &&
    !isBlank(values.secondaryCtaText) &&
    isBlank(values.secondaryCtaLink)
  ) {
    errors.secondaryCtaLink = 'A secondary CTA with a label needs a link.'
  }

  // Checked after the presence rules so a missing link is reported as missing
  // rather than as malformed.
  if (validatesPrimaryCta && errors.ctaLink === undefined) {
    const shapeError = validateCtaLinkShape(values.ctaLink)
    if (shapeError !== null) {
      errors.ctaLink = shapeError
    }
  }
  if (validatesSecondaryCta && errors.secondaryCtaLink === undefined) {
    const shapeError = validateCtaLinkShape(values.secondaryCtaLink)
    if (shapeError !== null) {
      errors.secondaryCtaLink = shapeError
    }
  }

  const prefixError = validatePrefixes(parseList(values.routePrefixes))
  if (prefixError !== null) {
    errors.routePrefixes = prefixError
  }
  const excludeError = validatePrefixes(parseList(values.routeExcludePrefixes))
  if (excludeError !== null) {
    errors.routeExcludePrefixes = excludeError
  }

  const localeError = validateLocales(parseList(values.locales))
  if (localeError !== null) {
    errors.locales = localeError
  }

  const startInstant = toInstant(values.startDate, values.startTime)
  if (isBlank(values.startDate)) {
    errors.startDate = 'Start date is required.'
  } else if (startInstant === null) {
    errors.startDate = 'Start date and time could not be read — set both.'
  }

  if (!isBlank(values.endDate)) {
    const endInstant = toInstant(values.endDate, values.endTime)
    if (endInstant === null) {
      errors.endDate = 'End date and time could not be read — set both, or clear the end date.'
    } else if (startInstant !== null && Date.parse(endInstant) <= Date.parse(startInstant)) {
      errors.endDate = 'End must be after the start.'
    }
  }

  if (placementSupports(values.placement, 'countdown')) {
    if (isBlank(values.countdownDate)) {
      // A time or a caption with no day is a half-authored countdown, and the
      // save would drop it silently: `countdown_target` is what the renderer
      // gates on, so the other two would be stored and never drawn.
      if (!isBlank(values.countdownTime)) {
        errors.countdownDate = 'Set a countdown date, or clear the time as well.'
      }
    } else {
      const countdownInstant = toInstant(values.countdownDate, values.countdownTime)
      if (countdownInstant === null) {
        errors.countdownDate =
          'Countdown date and time could not be read — set both, or clear the date.'
      } else if (
        startInstant !== null &&
        Date.parse(countdownInstant) <= Date.parse(startInstant)
      ) {
        // Not merely odd: the tiles would be expired for the slide's whole life,
        // so the operator would ship a countdown nobody ever sees counting.
        errors.countdownDate = 'The countdown must end after the announcement starts.'
      }
    }

    if (values.countdownLabel.trim().length > COUNTDOWN_LABEL_MAX_LENGTH) {
      errors.countdownLabel = `Countdown caption must be ${COUNTDOWN_LABEL_MAX_LENGTH} characters or fewer.`
    }
  }

  const autoClose = values.autoCloseSeconds.trim()
  if (!isBlank(autoClose) && placementSupports(values.placement, 'autoClose')) {
    const parsed = Number(autoClose)
    if (!Number.isInteger(parsed) || parsed <= 0 || parsed > AUTO_CLOSE_MAX_SECONDS) {
      errors.autoCloseSeconds = `Auto-close must be a whole number of seconds between 1 and ${AUTO_CLOSE_MAX_SECONDS}, or blank.`
    }
  }

  const pacingGap = values.pacingGapValue.trim()
  if (!isBlank(pacingGap) && placementSupports(values.placement, 'pacingGap')) {
    const parsed = Number(pacingGap)
    const largest = pacingGapMaxValue(values.pacingGapUnit)

    if (!Number.isInteger(parsed) || parsed < 1 || parsed > largest) {
      errors.pacingGap = `The gap must be a whole number between 1 and ${largest} ${PACING_GAP_UNIT_LABEL[values.pacingGapUnit]}, or blank for no gap.`
    }
  }

  const titleSize = values.titleSizePx.trim()
  if (
    !isBlank(titleSize) &&
    placementSupports(values.placement, 'titleSize') &&
    parseTitleSize(titleSize) === null
  ) {
    errors.titleSizePx = `Title size must be a whole number between ${TITLE_SIZE_MIN_PX} and ${TITLE_SIZE_MAX_PX} px, or blank for the default.`
  }

  const priority = values.priority.trim()
  if (!isBlank(priority)) {
    const parsed = Number(priority)
    if (!Number.isInteger(parsed)) {
      errors.priority = 'Priority must be a whole number.'
    } else if (parsed < PRIORITY_MIN || parsed > PRIORITY_MAX) {
      errors.priority = `Priority must be between ${PRIORITY_MIN} and ${PRIORITY_MAX}.`
    }
  }

  if (values.audiencePlans.length > 0 && values.audience !== 'paid') {
    errors.audience = 'Select Paid accounts only to target plans.'
  }
  if (values.audience === 'all' && values.audienceInverted) {
    errors.audience = 'Select a specific audience before excluding it.'
  }

  // Both bounds are inclusive on the BE, so an equal pair is a real one-day
  // window rather than an empty one — only a strict `>` is wrong.
  const minAge = parseAgeBound(errors, 'minAccountAgeDays', values.minAccountAgeDays)
  const maxAge = parseAgeBound(errors, 'maxAccountAgeDays', values.maxAccountAgeDays)
  if (minAge !== null && maxAge !== null && minAge > maxAge) {
    errors.minAccountAgeDays = 'The minimum account age must not exceed the maximum.'
  }

  return Object.keys(errors).length === 0 ? { ok: true } : { ok: false, errors }
}

/**
 * Route targeting is a `startsWith` match on the BE, so a prefix that does not
 * begin with `/` can never match anything and the campaign shows nowhere — the
 * failure mode is silence, which is why this is an error and not a hint.
 */
const validatePrefixes = (prefixes: string[]): string | null => {
  const invalid = prefixes.find((prefix) => !prefix.startsWith('/'))
  if (invalid !== undefined) {
    return `Every route prefix must start with "/" — got "${invalid}".`
  }

  const tooLong = prefixes.find((prefix) => prefix.length > ROUTE_PREFIX_MAX_LENGTH)

  return tooLong === undefined
    ? null
    : `Route prefixes must be at most ${ROUTE_PREFIX_MAX_LENGTH} characters.`
}

// Matches what the customer app stores as its language setting: a bare language
// code, optionally regioned. Anything else targets a locale nobody runs and the
// campaign never appears.
const LOCALE_PATTERN = /^[a-z]{2}(?:-[A-Za-z0-9]{2,8})?$/

const validateLocales = (locales: string[]): string | null => {
  const invalid = locales.find((locale) => !LOCALE_PATTERN.test(locale))

  return invalid === undefined
    ? null
    : `"${invalid}" is not a locale code — use e.g. en, ru, pt-BR.`
}

/**
 * Whether a dashboard banner is missing its backdrop. A warning, never a blocker —
 * the slide still renders, as copy on the bare surface. `hasImage` means a picked
 * file OR a stored image kept.
 *
 * Not a `PLACEMENT_CAPABILITIES` cell: that matrix answers "does this surface READ
 * the field", this answers "does it look unfinished without one". The carousel does
 * both, so folding them together would lose one.
 */
export const dashboardImageMissing = (
  placement: AnnouncementWritablePlacementChoice,
  hasImage: boolean,
): boolean => placement === 'dashboard_banner' && !hasImage

/**
 * Form values for a fresh announcement. The clock is the caller's to pass rather
 * than this module's to read, so nothing here derives from a value React cannot
 * observe changing; the zone it is projected into is the campaign's, not the
 * browser's.
 */
/**
 * The width the contract applies when the field is omitted, restated so a draft
 * shows the operator the width they will get before the server has seen it.
 */
const DEFAULT_MODAL_WIDTH: AnnouncementModalWidthChoice = 'default'

/**
 * What every stored toaster renders as, so a draft and an announcement saved
 * before the hero layout existed both start here. The BE refuses any other value
 * off a toaster, which is why the payload builders send this one for the rest.
 */
const DEFAULT_TOASTER_MEDIA_LAYOUT: AnnouncementToasterMediaLayoutChoice = 'inset'

export const emptyFormValues = (now: Date): AnnouncementFormValues => {
  const campaignNow = formatWallClock(instantToCampaignWallClock(now.getTime()))

  return {
    name: '',
    tag: '',
    // The dashboard banner is the surface marketing reaches for by default; a
    // toaster default meant every draft started on the one placement that
    // interrupts, and the mis-set ones that shipped were toasters.
    placement: 'dashboard_banner',
    title: '',
    titleHighlight: '',
    titleSizePx: '',
    modalWidth: DEFAULT_MODAL_WIDTH,
    toasterMediaLayout: DEFAULT_TOASTER_MEDIA_LAYOUT,
    toasterShowNewLabel: true,
    modalBackgroundEnabled: false,
    modalBackgroundStart: DEFAULT_MODAL_ARTWORK_SCRIM.start,
    modalBackgroundEnd: DEFAULT_MODAL_ARTWORK_SCRIM.end,
    modalBackgroundAngle: String(DEFAULT_MODAL_BACKGROUND.angle),
    modalBackgroundSize: DEFAULT_MODAL_BACKGROUND.size,
    modalBackgroundPosition: DEFAULT_MODAL_BACKGROUND.position,
    modalBackgroundRepeat: DEFAULT_MODAL_BACKGROUND.repeat,
    modalBackgroundMobileMode: DEFAULT_MODAL_BACKGROUND.mobile_mode,
    body: '',
    bodyStyle: 'plain',
    countdownDate: '',
    countdownTime: '',
    countdownLabel: '',
    countdownExpiry: 'hide_countdown',
    ctaText: '',
    ctaLink: '',
    ctaTarget: 'new_tab',
    secondaryCtaText: '',
    secondaryCtaLink: '',
    routePrefixes: '',
    routeExcludePrefixes: '',
    audience: 'all',
    audienceInverted: false,
    audiencePlans: [],
    insiderGate: 'any',
    minAccountAgeDays: '',
    maxAccountAgeDays: '',
    requiredFlag: '',
    requiredFlagInverted: false,
    locales: '',
    localesInverted: false,
    dismissible: true,
    // Seeded from the campaign zone, not the browser's: a new form must open on the
    // same clock the fields are read in, or an operator abroad starts one edit behind.
    startDate: campaignNow.date,
    startTime: campaignNow.time,
    endDate: '',
    endTime: '',
    autoCloseSeconds: '',
    pacingGapValue: '',
    pacingGapUnit: DEFAULT_PACING_GAP_UNIT,
    priority: '',
    // A new announcement starts staff-only. Releasing it is a deliberate second
    // action, which is what makes "create it and look at it in the real app" safe.
    isPreview: true,
    isPaused: false,
    blocks: [],
  }
}

/**
 * The draft fields an example or a pasted design may replace without asking.
 *
 * The surface alone. An operator picks it BEFORE pasting a link — the request is
 * shaped for it, and the answer is pinned back to it — so counting that choice as
 * work would withdraw the paste box from everyone who used the feature as
 * intended. Written as a deny list rather than as a list of the fields that DO
 * count, so a field added to this form joins the guard by default instead of
 * becoming a hole in it that nothing fails over.
 */
const REPLACEABLE_FIELDS: readonly (keyof AnnouncementFormValues)[] = ['placement']

/**
 * The one block key the block itself does not own.
 *
 * A media field makes the preview URL for whatever file its block holds and
 * writes it back into the draft a tick after the block mounts. That write
 * replaces the block an import wrote with one nothing typed, so comparing this
 * key reads the field's own bookkeeping as operator work — and withdrew the
 * paste box from every import that carried artwork, together with the
 * corrections that import's own notes ask for.
 */
const FIELD_OWNED_BLOCK_KEY = 'stagedUrl'

/** Whether two block drafts hold the same work, ignoring the field-owned key. */
const sameBlockEntry = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true
  }

  if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) {
    return false
  }

  const compared = new Set([...Object.keys(left), ...Object.keys(right)])
  compared.delete(FIELD_OWNED_BLOCK_KEY)

  return [...compared].every(
    (key) => (left as Record<string, unknown>)[key] === (right as Record<string, unknown>)[key],
  )
}

/**
 * Whether one draft field still holds what the form put there.
 *
 * Lists compare by length and then entry by entry. `audiencePlans` holds
 * plain numbers, and `blocks` holds drafts the editor
 * replaces wholesale on every edit, so identity answers all three — except for
 * the one key above, which a block's own field writes without the operator.
 */
const sameDraftField = (
  left: unknown,
  right: unknown,
  sameEntry: (leftEntry: unknown, rightEntry: unknown) => boolean = Object.is,
): boolean =>
  Array.isArray(left) && Array.isArray(right)
    ? left.length === right.length && left.every((entry, index) => sameEntry(entry, right[index]))
    : left === right

/**
 * Whether the draft still holds nothing an example or an import would destroy.
 *
 * Every field is compared against the PRISTINE draft the form opened with. The
 * guard used to read the campaign name and the blocks alone, and blocks are a
 * MODAL field — so an operator who chose a toaster, a strip banner or a dashboard
 * banner and typed a title and a body still counted as untouched, and an import
 * answering seconds later replaced that copy with nothing said.
 *
 * A baseline rather than a set of "is it empty" tests, because most of this form
 * starts non-empty: the modal width, the CTA target, the audience, the scrim
 * colours, and a start date seeded off the campaign clock. Against the values the
 * form itself seeded, every one of those reads as untouched until the operator
 * moves it — and once they do, it reads as work, which is what it is.
 */
export const isUntouchedDraft = (
  draft: AnnouncementFormValues,
  pristine: AnnouncementFormValues,
): boolean =>
  (Object.keys(pristine) as (keyof AnnouncementFormValues)[])
    .filter((field) => !REPLACEABLE_FIELDS.includes(field))
    .every((field) =>
      sameDraftField(
        draft[field],
        pristine[field],
        field === 'blocks' ? sameBlockEntry : undefined,
      ),
    )

/**
 * Every placement the form can author. `modal` joined the union once a renderer
 * existed for it; while it was absent, a stored modal row was projected onto
 * `toaster` here and `buildUpdatePatch` — which diffs against the raw record, not
 * this projection — pushed that rewrite to the server on the next save. Removing
 * `modal` from this list again would silently downgrade real modal rows and
 * strand their blocks, so it stays as long as the modal surface renders.
 *
 * The `toaster` fallback below is now only for a placement this build does not
 * know: a row written by a newer deploy still opens on something the operator can
 * see rather than an empty select.
 */
export const WRITABLE_PLACEMENTS: readonly AnnouncementWritablePlacementChoice[] = [
  'toaster',
  'strip_banner',
  'dashboard_banner',
  'modal',
]

export const toWritablePlacement = (value: string): AnnouncementWritablePlacementChoice =>
  WRITABLE_PLACEMENTS.find((placement) => placement === value) ?? 'toaster'

/** Form values for an existing record, as the edit page loaded it. */
export const formValuesFrom = (existing: AnnouncementAdminSchema): AnnouncementFormValues => {
  const start = fromInstant(existing.start_date)
  const end = fromInstant(existing.end_date)
  const countdown = fromInstant(existing.countdown_target)
  // Read back as the largest unit that divides the stored hours evenly, so a gap
  // authored as "2 weeks" does not reopen as "336 hours".
  const pacingGap = pacingGapFromHours(existing.min_hours_since_previous)
  const background = existing.modal_background
  const gradientDefaults =
    background === null || background === undefined
      ? DEFAULT_MODAL_ARTWORK_SCRIM
      : DEFAULT_MODAL_BACKGROUND

  return {
    name: existing.name,
    tag: existing.tag ?? '',
    placement: toWritablePlacement(existing.placement),
    title: existing.title,
    titleHighlight: existing.title_highlight ?? '',
    titleSizePx:
      existing.title_size_px === null || existing.title_size_px === undefined
        ? ''
        : String(existing.title_size_px),
    modalWidth: existing.modal_width ?? DEFAULT_MODAL_WIDTH,
    toasterMediaLayout: existing.toaster_media_layout ?? DEFAULT_TOASTER_MEDIA_LAYOUT,
    toasterShowNewLabel: existing.toaster_show_new_label ?? true,
    modalBackgroundEnabled: background !== null && background !== undefined,
    modalBackgroundStart: background?.start ?? gradientDefaults.start,
    modalBackgroundEnd: background?.end ?? gradientDefaults.end,
    modalBackgroundAngle: String(background?.angle ?? DEFAULT_MODAL_BACKGROUND.angle),
    modalBackgroundSize: background?.size ?? DEFAULT_MODAL_BACKGROUND.size,
    modalBackgroundPosition: background?.position ?? DEFAULT_MODAL_BACKGROUND.position,
    modalBackgroundRepeat: background?.repeat ?? DEFAULT_MODAL_BACKGROUND.repeat,
    modalBackgroundMobileMode: background?.mobile_mode ?? DEFAULT_MODAL_BACKGROUND.mobile_mode,
    body: existing.body ?? '',
    bodyStyle: existing.body_style,
    countdownDate: countdown.date,
    countdownTime: countdown.time,
    countdownLabel: existing.countdown_label ?? '',
    countdownExpiry: existing.countdown_expiry ?? 'hide_countdown',
    ctaText: existing.cta_text ?? '',
    ctaLink: existing.cta_link ?? '',
    ctaTarget: existing.cta_target,
    secondaryCtaText: existing.secondary_cta_text ?? '',
    secondaryCtaLink: existing.secondary_cta_link ?? '',
    routePrefixes: formatList(existing.route_prefixes),
    routeExcludePrefixes: formatList(existing.route_exclude_prefixes),
    // Everyone narrowed by plans reaches paid subscribers alone
    // on the BE, so it reopens as the Paid audience the matrix shows under.
    audience:
      existing.audience === 'all' && storedPlans(existing).length > 0 ? 'paid' : existing.audience,
    audienceInverted: existing.audience_inverted ?? false,
    audiencePlans: storedPlans(existing),
    insiderGate: existing.insider_gate ?? 'any',
    // Spelled out rather than `?? ''` because a zero floor is a real gate ("only
    // accounts younger than a day"), and `0 ?? ''` is fine while `0 || ''` is not
    // — matching how `titleSizePx` and `autoCloseSeconds` guard the same trap.
    minAccountAgeDays:
      existing.min_account_age_days === null || existing.min_account_age_days === undefined
        ? ''
        : String(existing.min_account_age_days),
    maxAccountAgeDays:
      existing.max_account_age_days === null || existing.max_account_age_days === undefined
        ? ''
        : String(existing.max_account_age_days),
    requiredFlag: existing.required_flag ?? '',
    requiredFlagInverted: existing.required_flag_inverted ?? false,
    locales: formatList(existing.locales),
    localesInverted: existing.locales_inverted ?? false,
    dismissible: existing.dismissible,
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    autoCloseSeconds:
      existing.auto_close_seconds === null || existing.auto_close_seconds === undefined
        ? ''
        : String(existing.auto_close_seconds),
    pacingGapValue: pacingGap.value,
    pacingGapUnit: pacingGap.unit,
    priority: String(existing.priority),
    isPreview: existing.is_preview,
    isPaused: existing.is_paused,
    blocks: blockDraftsFrom(existing.blocks),
  }
}
